import { useState, useCallback, type ChangeEvent, type DragEvent } from 'react';
import { FileText, Upload, Download, Trash2, RefreshCw, X } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from './common/Button';
import { Card } from './common/Card';
import type { Project } from '../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface AttachmentCardProps {
  project: Project;
  canModify: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
  onDownload: () => Promise<void>;
  isUploading?: boolean;
  isRemoving?: boolean;
  isDownloading?: boolean;
}

export function AttachmentCard({
  project,
  canModify,
  onUpload,
  onRemove,
  onDownload,
  isUploading = false,
  isRemoving = false,
  isDownloading = false,
}: AttachmentCardProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasPdf = !!project.pdf_key;

  const validateFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') {
      return 'Only PDF files are allowed';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be 10MB or less';
    }
    return null;
  };

  const handleFile = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    try {
      await onUpload(file);
    } catch {
      setError('Failed to upload file. Please try again.');
    }
  }, [onUpload]);

  const handleDrop = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleFile]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (isoString: string): string => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Uploading state
  if (isUploading) {
    return (
      <Card className="!p-4">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-action animate-spin" />
          <span className="text-wolf-300">Uploading...</span>
        </div>
      </Card>
    );
  }

  // Has PDF attached
  if (hasPdf) {
    return (
      <Card className="!p-4">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-action flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-wolf-100 font-medium truncate">
              {project.pdf_filename || 'attachment.pdf'}
            </p>
            <p className="text-wolf-500 text-sm">
              {project.pdf_size && formatFileSize(project.pdf_size)}
              {project.pdf_uploaded_at && ` • ${formatDate(project.pdf_uploaded_at)}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="secondary"
              size="sm"
              onClick={onDownload}
              loading={isDownloading}
            >
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
            {canModify && (
              <>
                <label className="cursor-pointer inline-flex items-center justify-center font-semibold text-sm transition-all duration-300 px-4 py-2 text-xs bg-transparent border border-wolf-700 text-wolf-300 hover:border-wolf-500 hover:text-wolf-100 focus-within:ring-2 focus-within:ring-action focus-within:ring-offset-2 focus-within:ring-offset-navy">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Replace
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={onRemove}
                  loading={isRemoving}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // No PDF - empty state
  if (!canModify) {
    return (
      <Card className="!p-4">
        <div className="text-wolf-500 text-sm">
          No attachments
        </div>
      </Card>
    );
  }

  // Upload drop zone
  return (
    <Card className="!p-2">
      <label
        className={clsx(
          'flex flex-col items-center justify-center px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
          isDragOver
            ? 'border-action bg-action/10'
            : 'border-wolf-700/50 hover:border-action/50 hover:bg-wolf-900/30',
          error && 'border-red-500'
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <Upload className={clsx('w-8 h-8 mb-2', isDragOver ? 'text-action' : 'text-wolf-500')} />
        <span className="text-wolf-300 text-sm">Drag & drop a PDF or click to upload</span>
        <span className="text-wolf-500 text-xs mt-1">Max 10MB</span>
        <input
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileChange}
          className="hidden"
        />
      </label>
      {error && (
        <div className="mt-2 flex items-center gap-2 text-red-400 text-sm">
          <X className="w-4 h-4" />
          {error}
        </div>
      )}
    </Card>
  );
}
